import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getCompanyPlan, upgradePlan } from '../lib/api';
import { toast } from 'sonner';

const PlanContext = createContext(null);

const PLAN_DEFAULTS = {
  plan: 'starter',
  plan_name: 'Starter',
  price_usd: 99,
  features: ['packages', 'customers', 'basic_reports', 'tracking', 'scanner', 'csv_import', 'batch_status'],
  max_clients: 100,
  max_users: 3,
  current_clients: 0,
  current_users: 0,
  subscription_status: 'active',
};

export const PlanProvider = ({ children }) => {
  const { user } = useAuth();
  const [planInfo, setPlanInfo] = useState(PLAN_DEFAULTS);
  const [planLoading, setPlanLoading] = useState(false);

  const refreshPlan = useCallback(async () => {
    if (!user) return;
    setPlanLoading(true);
    try {
      const res = await getCompanyPlan();
      setPlanInfo(res.data);
    } catch {
      // keep defaults
    } finally {
      setPlanLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshPlan();
  }, [refreshPlan]);

  const hasFeature = useCallback((feature) => {
    const features = planInfo.features || [];
    return features.includes('all') || features.includes(feature);
  }, [planInfo]);

  const isAtClientLimit = planInfo.max_clients !== -1 && planInfo.current_clients >= planInfo.max_clients;
  const isAtUserLimit = planInfo.max_users !== -1 && planInfo.current_users >= planInfo.max_users;
  const clientUsagePercent = planInfo.max_clients === -1 ? 0 : Math.round((planInfo.current_clients / planInfo.max_clients) * 100);
  const userUsagePercent = planInfo.max_users === -1 ? 0 : Math.round((planInfo.current_users / planInfo.max_users) * 100);

  const handleUpgrade = useCallback(async () => {
    try {
      const res = await upgradePlan();
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start upgrade. Please try again.');
    }
  }, []);

  return (
    <PlanContext.Provider value={{
      ...planInfo,
      planLoading,
      hasFeature,
      isAtClientLimit,
      isAtUserLimit,
      clientUsagePercent,
      userUsagePercent,
      refreshPlan,
      handleUpgrade,
    }}>
      {children}
    </PlanContext.Provider>
  );
};

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};
